/**
 * @fileoverview Pure task-filter state + predicate helpers (MOR-696 Stage 2)
 *
 * Split out of `task-filter-bar.tsx` (react-refresh/only-export-components) —
 * `TaskFilterState` plus the filter-state helpers and the pure predicate the
 * hosting routes run over the `CalendarEventRow[]` stream, so the same filter
 * logic backs every surface and the saved-view restore path.
 */

import {
  addWeeks,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  format,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
} from "date-fns";

import type { CalendarEventRow } from "@/calendar/eventAdapter";
import type { JobStatus } from "@cvx/jobs/schema";
import type { KeyDateCategory } from "@cvx/keyDates/jobKeyDates/schema";
import type { TaskDueRange } from "@cvx/tasks/taskViews/schema";

/** Client-side filter state. Empty arrays / blank search ⇒ no constraint. */
export interface TaskFilterState {
  statusIds: string[];
  assigneeUserIds: string[];
  labelIds: string[];
  priorities: number[];
  /** Category ("type") filter — MOR-707 replaced the event/task kind split. */
  categories: KeyDateCategory[];
  /** Job lifecycle filter (cross-job surfaces only): only rows whose job
   * currently holds one of these statuses. */
  jobStatuses: JobStatus[];
  overdueOnly: boolean;
  /** Only tasks due (or planned) today. */
  today: boolean;
  /** Hide rows that are subtasks of another task (MOR-752). */
  hideSubtasks: boolean;
  /** Due-date range — rolling preset or fixed custom dates (MOR-752). */
  dueRange: TaskDueRange | null;
  search: string;
}

export function emptyFilters(): TaskFilterState {
  return {
    statusIds: [],
    assigneeUserIds: [],
    labelIds: [],
    priorities: [],
    categories: [],
    jobStatuses: [],
    overdueOnly: false,
    today: false,
    hideSubtasks: false,
    dueRange: null,
    search: "",
  };
}

/** True when no dimension constrains the result set. */
export function isEmptyFilters(f: TaskFilterState): boolean {
  return (
    f.statusIds.length === 0 &&
    f.assigneeUserIds.length === 0 &&
    f.labelIds.length === 0 &&
    f.priorities.length === 0 &&
    f.categories.length === 0 &&
    f.jobStatuses.length === 0 &&
    !f.overdueOnly &&
    !f.today &&
    !f.hideSubtasks &&
    f.dueRange === null &&
    f.search.trim() === ""
  );
}

/**
 * Resolve a due-range filter to inclusive ISO date bounds, relative to `now`.
 * Rolling presets recompute every render so a saved "this week" view always
 * shows the current week. Returns null when a custom range has no dates yet
 * (an empty chip constrains nothing).
 */
export function resolveDueRange(
  range: TaskDueRange,
  now: Date = new Date(),
): { from: string; to: string } | null {
  const iso = (d: Date) => format(d, "yyyy-MM-dd");
  switch (range.preset) {
    case "thisWeek":
      return { from: iso(startOfWeek(now)), to: iso(endOfWeek(now)) };
    case "thisFortnight":
      return {
        from: iso(startOfWeek(now)),
        to: iso(endOfWeek(addWeeks(now, 1))),
      };
    case "thisMonth":
      return { from: iso(startOfMonth(now)), to: iso(endOfMonth(now)) };
    case "thisQuarter":
      return { from: iso(startOfQuarter(now)), to: iso(endOfQuarter(now)) };
    case "custom":
      if (!range.from && !range.to) return null;
      return { from: range.from ?? "0000-01-01", to: range.to ?? "9999-12-31" };
  }
}

/** Pure predicate-applying filter over the calendar/task row stream. */
export function applyTaskFilters(
  events: CalendarEventRow[],
  f: TaskFilterState,
): CalendarEventRow[] {
  const statusSet = new Set(f.statusIds);
  const assigneeSet = new Set(f.assigneeUserIds);
  const labelSet = new Set(f.labelIds);
  const prioritySet = new Set(f.priorities);
  const categorySet = new Set(f.categories);
  const jobStatusSet = new Set(f.jobStatuses);
  const todayIso = f.today ? format(new Date(), "yyyy-MM-dd") : null;
  const dueRange = f.dueRange ? resolveDueRange(f.dueRange) : null;
  const includeOverdue = f.dueRange?.includeOverdue ?? false;
  const search = f.search.trim().toLowerCase();

  return events.filter((e) => {
    if (
      statusSet.size > 0 &&
      (!e.statusId || !statusSet.has(String(e.statusId)))
    ) {
      return false;
    }
    if (
      assigneeSet.size > 0 &&
      (!e.assigneeUserId || !assigneeSet.has(String(e.assigneeUserId)))
    ) {
      return false;
    }
    if (
      labelSet.size > 0 &&
      !e.labelIds.some((id) => labelSet.has(String(id)))
    ) {
      return false;
    }
    if (prioritySet.size > 0 && !prioritySet.has(e.priority ?? 0)) {
      return false;
    }
    if (categorySet.size > 0 && !categorySet.has(e.category)) {
      return false;
    }
    // Rows from single-job feeds carry no jobStatus stamp — the dimension
    // only constrains cross-job rows (listForOrg stamps every row).
    if (
      jobStatusSet.size > 0 &&
      e.jobStatus &&
      !jobStatusSet.has(e.jobStatus)
    ) {
      return false;
    }
    if (f.overdueOnly && !e.overdue) {
      return false;
    }
    if (f.hideSubtasks && e.parentTaskId) {
      return false;
    }
    if (todayIso && e.date !== todayIso && e.plannedDate !== todayIso) {
      return false;
    }
    if (dueRange) {
      const inRange = e.date >= dueRange.from && e.date <= dueRange.to;
      // "+ overdue" widens the window to catch overdue work from before it.
      if (!inRange && !(includeOverdue && e.overdue)) return false;
    }
    if (search) {
      const hay = `${e.title} ${e.description ?? ""}`.toLowerCase();
      if (!hay.includes(search)) return false;
    }
    return true;
  });
}

/**
 * Categories that are calendar-only reference markers rather than actionable
 * work. The task board/list hides these by default (a removable "Hide events"
 * toggle) so the board reads as a work board; the Calendar still shows them.
 */
export const CALENDAR_ONLY_CATEGORIES: ReadonlySet<KeyDateCategory> = new Set([
  "appointment",
  "meeting",
  "milestone",
  "investigation_window",
]);
